import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getConsentJsonHeaders } from "./consentHeaders";
import { getConsentGateItemsKey, type ConsentGateItem } from "./useGetConsentGateItems";
import { getContractualAcceptancesKey } from "./useGetContractualAcceptances";

type AcceptConsentGateInput = {
    versionIds: number[];
};

type ConsentGateHydraCache = {
    data?: {
        "hydra:member"?: ConsentGateItem[];
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

type ConsentGateCache = ConsentGateItem[] | ConsentGateHydraCache | undefined;

export const applyConsentGateCacheUpdate = (
    currentItems: ConsentGateCache,
    acceptedVersionIds: Set<number>,
): ConsentGateCache => {
    const filterAcceptedItems = (items: ConsentGateItem[]) =>
        items.filter((item) => !acceptedVersionIds.has(item.versionId));

    if (currentItems === undefined) {
        return currentItems;
    }

    if (Array.isArray(currentItems)) {
        return filterAcceptedItems(currentItems);
    }

    const hydraMembers = currentItems.data?.["hydra:member"];

    if (!Array.isArray(hydraMembers)) {
        return currentItems;
    }

    return {
        ...currentItems,
        data: {
            ...currentItems.data,
            "hydra:member": filterAcceptedItems(hydraMembers),
        },
    };
};

export const useAcceptConsentGate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: AcceptConsentGateInput) => {
            const { versionIds } = options;

            return loggedInApiCall.post(
                "/api/users/me/consent-gate/accept",
                { versionIds },
                {
                    headers: getConsentJsonHeaders(),
                },
            );
        },
        onSuccess: async (_response, { versionIds }) => {
            const acceptedVersionIds = new Set(versionIds);

            queryClient.setQueryData<ConsentGateCache>([getConsentGateItemsKey], (currentItems) =>
                applyConsentGateCacheUpdate(currentItems, acceptedVersionIds),
            );

            await Promise.all([
                queryClient.invalidateQueries([getConsentGateItemsKey]),
                queryClient.invalidateQueries([getContractualAcceptancesKey]),
            ]);
        },
    });
};
